/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { memo, useEffect, useRef, useCallback } from 'react';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import VideoIcon from '@atlaskit/icon/core/video';
import Lozenge from '@atlaskit/lozenge';
import { Flex } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { useIntl } from '@atlassian/jira-intl';
import { useJiraLoomRecorderEntryPoint } from '@atlassian/jira-loom-utils/src/controllers/use-jira-loom-recorder-entrypoint/index.tsx';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useIssueTypeField } from '@atlassian/jira-issue-field-issue-type/src/services/index.tsx';
import messages from '../messages.tsx';
import type { Props } from '../types.tsx';

export const CoUseRecordItem = memo<Props>(({ onInsertLink }) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'dropdownItem',
	});
	const issueKey = useIssueKey();
	const [{ value: issueType }] = useIssueTypeField({ issueKey });

	const domRef = useRef<HTMLElement | null>(null);
	const insertTargetMarkerRef = useRef<string | null | undefined>(null);

	useEffect(() => {
		/* Once the user clicks the record button (this component),
		 * the menu closes and this component, with all its hooks, unmounts.
		 * When recording ends renderOnInsertFallbackWhen will run in order to determine
		 * whether it is still possible to insert the link or not.
		 * This is determined by whether the issue from which recording has started is still mounted.
		 * This effect saves an identifier that the callback can use when it runs.
		 */
		insertTargetMarkerRef.current = domRef.current
			?.closest('[data-loom-insert-target-marker]')
			?.getAttribute('data-loom-insert-target-marker');

		if (!insertTargetMarkerRef.current) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'loomSdkInsertTargetMarker notFound', {
				touchpoint: 'loomSectionActionsMenuAddButton',
			});
		}
	}, [createAnalyticsEvent]);

	const { openLoomRecorder, recorderState } = useJiraLoomRecorderEntryPoint({
		entryPointLocation: 'jiraIssueView_loomSectionActionsMenuAddButton',
		onInsert: onInsertLink,
		// intentionally not localized because Loom RecordSDK does not support i18n
		insertButtonText: 'Add video',
		renderOnInsertFallbackWhen: () => {
			if (typeof document !== 'undefined') {
				return (
					!insertTargetMarkerRef.current ||
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					document
						.querySelector('[data-loom-insert-target-marker]')
						?.getAttribute('data-loom-insert-target-marker') !== insertTargetMarkerRef.current
				);
			}
			return false;
		},
		experimentalFeatures: {
			jiraIssueType: issueType?.name,
		},
	});
	const recordLoomVideo = useCallback(() => {
		fireUIAnalytics(analyticsEvent, 'add-icon-button-dropdown-record-a-video-button');
		openLoomRecorder?.();
	}, [analyticsEvent, openLoomRecorder]);

	return (
		<DropdownItem
			key="loom-section-actions-menu-recording-button"
			ref={domRef}
			elemBefore={
				recorderState === 'initialising' ? (
					<Spinner
						size="medium"
						label={formatMessage(messages.loading)}
						testId="issue-view-base.content.loom-videos.ui.co-use.loom-actions-menu.record-new-loom-item.co-use.recording-button-spinner"
					/>
				) : (
					<VideoIcon
						label={formatMessage(messages.videoIcon)}
						color="currentColor"
						spacing="spacious"
					/>
				)
			}
			onClick={recordLoomVideo}
			isDisabled={recorderState === 'initialising'}
		>
			<Flex alignItems="center" gap="space.050">
				{formatMessage(messages.newLoomVideo)}
				<Lozenge appearance="new">{formatMessage(messages.newLozenge)}</Lozenge>
			</Flex>
		</DropdownItem>
	);
});
